import { useContext, useLayoutEffect } from 'react'

import { ArcadeModalActionContext, ArcadeModalActionOverride } from './ArcadeModalActionContext'

/**
 * Use this hook to override the actions of an ArcadeModal or ArcadeModalPane.
 *
 * You can either:
 * 1. Override button props (back/close buttons)
 * 2. Provide a completely custom header component
 *
 * @param override - The action override configuration
 *
 * @example
 * ```tsx
 * // Override button props
 * useArcadeModalActionOverride({
 *   type: 'buttons',
 *   backButtonProps: {
 *     onBack: () => console.log('Custom back'),
 *     ariaLabel: 'Go back'
 *   },
 *   closeButtonProps: {
 *     onClose: () => console.log('Custom close'),
 *     ariaLabel: 'Close modal'
 *   }
 * })
 *
 * // Override with custom header
 * useArcadeModalActionOverride({
 *   type: 'customHeader',
 *   CustomHeader: <MyCustomHeader />
 * })
 * ```
 */
export const useArcadeModalActionOverride = (override: ArcadeModalActionOverride) => {
  const { setActionOverride } = useContext(ArcadeModalActionContext)

  useLayoutEffect(() => {
    setActionOverride(override)
  }, [override, setActionOverride])

  useLayoutEffect(() => {
    return () => {
      // Clean up modal action override in a separate useLayoutEffect, so that it only runs on unmount
      setActionOverride(undefined)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
}
